/**
 * =============================================================================
 * THEME PRESETS
 * =============================================================================
 *
 * Defines all theme variations for the global theme switcher.
 * Each preset includes:
 * - Button class hierarchy (which Bootstrap classes to use)
 * - Color palette (theme color values)
 *
 * Button Hierarchy (5 Roles):
 * - primary: Main CTA (Save, Create, Submit)
 * - secondary: Supporting actions (Dropdowns, Export)
 * - tertiary: Low-emphasis (table row actions, inline actions)
 * - danger: Destructive actions (Delete)
 * - cancel: Dismiss actions (Cancel, Close)
 */

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    blackbox: string;
    success: string;
    warning: string;
    danger: string;
    info: string;
  };
  buttonClasses: {
    primary: string;
    secondary: string;
    tertiary: string;
    danger: string;
    cancel: string;
  };
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Production baseline - solid primary, outline secondary, orange action',
    colors: {
      primary: '#33658a',
      secondary: '#6c757d',
      blackbox: '#ff9933',
      success: '#198754',
      warning: '#fd9d17',
      danger: '#f44336',
      info: '#669fcd'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-outline-primary',
      tertiary: 'btn-outline-secondary',
      danger: 'btn-outline-danger',
      cancel: 'btn-secondary'
    }
  },
  {
    id: 'orange-tertiary',
    name: 'Orange Tertiary',
    description: 'Orange as low-emphasis button',
    colors: {
      primary: '#33658a',
      secondary: '#6c757d',
      blackbox: '#ff9933',
      success: '#198754',
      warning: '#fd9d17',
      danger: '#f44336',
      info: '#669fcd'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-outline-primary',
      tertiary: 'btn-outline-blackbox',
      danger: 'btn-outline-danger',
      cancel: 'btn-secondary'
    }
  },
  {
    id: 'orange-prominent',
    name: 'Orange Prominent',
    description: 'Orange promoted to secondary level - more visible',
    colors: {
      primary: '#33658a',
      secondary: '#6c757d',
      blackbox: '#ff9933',
      success: '#198754',
      warning: '#fd9d17',
      danger: '#f44336',
      info: '#669fcd'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-outline-blackbox',
      tertiary: 'btn-outline-secondary',
      danger: 'btn-outline-danger',
      cancel: 'btn-secondary'
    }
  },
  {
    id: 'orange-subtle',
    name: 'Orange Subtle',
    description: 'Orange as outline - less overpowering',
    colors: {
      primary: '#33658a',
      secondary: '#6c757d',
      blackbox: '#ff9933',
      success: '#198754',
      warning: '#fd9d17',
      danger: '#f44336',
      info: '#669fcd'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-outline-primary',
      tertiary: 'btn-outline-secondary',
      danger: 'btn-outline-danger',
      cancel: 'btn-secondary'
    }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'All outline buttons - light, modern aesthetic',
    colors: {
      primary: '#33658a',
      secondary: '#6c757d',
      blackbox: '#ff9933',
      success: '#198754',
      warning: '#fd9d17',
      danger: '#f44336',
      info: '#669fcd'
    },
    buttonClasses: {
      primary: 'btn-outline-primary',
      secondary: 'btn-outline-secondary',
      tertiary: 'btn-light',
      danger: 'btn-outline-danger',
      cancel: 'btn-light'
    }
  },
  {
    id: 'high-contrast',
    name: 'High Contrast',
    description: 'Solid buttons for maximum visibility',
    colors: {
      primary: '#1e40af',
      secondary: '#374151',
      blackbox: '#ea580c',
      success: '#15803d',
      warning: '#d97706',
      danger: '#dc2626',
      info: '#0284c7'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      tertiary: 'btn-outline-secondary',
      danger: 'btn-danger',
      cancel: 'btn-secondary'
    }
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Professional navy blue palette',
    colors: {
      primary: '#1e3a5f',
      secondary: '#64748b',
      blackbox: '#f59e0b',
      success: '#059669',
      warning: '#d97706',
      danger: '#dc2626',
      info: '#0284c7'
    },
    buttonClasses: {
      primary: 'btn-primary',
      secondary: 'btn-outline-primary',
      tertiary: 'btn-outline-secondary',
      danger: 'btn-outline-danger',
      cancel: 'btn-secondary'
    }
  }
];
